import { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { isPdfFile } from '../../lib/pdfUtils';
import { logAppError } from '../../lib/logAppError';

const CACHE_NAME = 'car-app-image-cache-v1';

interface CachedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    fallback?: React.ReactNode;
}

/**
 * A component that caches images locally using the browser's Cache API.
 * Future requests for the same URL will be served from the local cache.
 */
export function CachedImage({ src, fallback, ...props }: CachedImageProps) {
    if (src && isPdfFile(src) && !src.includes('.thumb.jpg')) {
        return (
            <div
                className={`flex items-center justify-center bg-gray-100 ${props.className || ''}`}
                style={props.style}
                title="PDF Document"
            >
                <FileText className="w-1/2 h-1/2 text-gray-500" />
            </div>
        );
    }

    const [localSrc, setLocalSrc] = useState<string | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!src) return;

        let active = true;
        let objectUrl: string | null = null;

        async function load() {
            if (src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('http://localhost') || src.startsWith('https://localhost')) {
                if (active) setLocalSrc(src);
                return;
            }
            try {
                // Check if Cache API is supported
                if (typeof caches !== 'undefined') {
                    const cache = await caches.open(CACHE_NAME);
                    const match = await cache.match(src);

                    if (match) {
                        const blob = await match.blob();
                        objectUrl = URL.createObjectURL(blob);
                        if (active) {
                            setLocalSrc(objectUrl);
                        }
                        return;
                    }
                } else {
                    // Fallback if no caches API
                    if (active) setLocalSrc(src);
                    return;
                }

                // If we are here, caches exist but image wasn't found
                const cache = await caches.open(CACHE_NAME);
                // If not in cache, fetch it
                const response = await fetch(src);

                if (response.ok) {
                    // Cache the successful response
                    await cache.put(src, response.clone());

                    const blob = await response.blob();
                    objectUrl = URL.createObjectURL(blob);
                    if (active) {
                        setLocalSrc(objectUrl);
                    }
                } else {
                    // Log non-OK responses (e.g. 404 from R2)
                    const isR2Url = src.includes('.r2.') || src.includes('r2.cloudflarestorage') || src.includes('mycarcosts') || src.includes('.workers.dev');
                    if (isR2Url) {
                        logAppError({
                            source: 'file_fetch',
                            statusCode: response.status,
                            path: src,
                            method: 'GET',
                            message: `Image fetch failed: ${response.status} ${response.statusText}`,
                            context: 'CachedImage',
                        }).catch(() => {});
                    }
                    // Fallback to original URL if fetch failed but returned some response
                    if (active) setLocalSrc(src);
                }
            } catch (err) {
                console.error('Error in CachedImage:', err);
                // Fallback to original URL on error (e.g. CORS issues)
                if (active) setLocalSrc(src);
            }
        }

        load();

        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [src]);

    if (error || !localSrc) {
        if (fallback) return <>{fallback}</>;
        // While loading, show a skeleton or placeholder
        return (
            <div
                className={`animate-pulse bg-gray-100 flex items-center justify-center ${props.className || ''}`}
                style={{ ...props.style, minWidth: '20px', minHeight: '20px' }}
            >
                <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin opacity-50" />
            </div>
        );
    }

    return (
        <img
            src={localSrc}
            {...props}
            onError={() => {
                if (localSrc !== src) {
                    setLocalSrc(src); // Try original source if cached blob fails
                } else {
                    // Log 404-like errors from R2 when image ultimately fails
                    const isR2Url = src.includes('.r2.') || src.includes('r2.cloudflarestorage') || src.includes('mycarcosts') || src.includes('.workers.dev');
                    if (isR2Url) {
                        logAppError({
                            source: 'file_fetch',
                            statusCode: 404,
                            path: src,
                            method: 'GET',
                            message: 'Image failed to load (onError)',
                            context: 'CachedImage',
                        }).catch(() => {});
                    }
                    setError(true);
                }
            }}
        />
    );
}
